import { getTotalPrice as getTotalPriceUtil, multiplyMoney } from '@shared/lib/money';
import { createSelector } from 'reselect';
import { Money } from '../../../types';
import { currentCurrency } from '../../../utils';
import { CheckoutState } from '../reducers';
import { getSelectedServices } from '../selectedServices/selectors';
import { getBaggageDelivery, getBusinessLounge, getCapsuleHotel } from '../order/selectors';
import {
	CheckoutOrderFragment,
	GdsService_OrderAdditionalServiceGdsServiceVip_Fragment,
	GdsService_OrderAdditionalServiceGdsServiceMisc_Fragment,
	OrderPriceFragment,
	TravellerSelectedServices
} from '@websky/graphql';

type OrderServiceFragment =
	| GdsService_OrderAdditionalServiceGdsServiceVip_Fragment
	| GdsService_OrderAdditionalServiceGdsServiceMisc_Fragment;

export const zeroPrice: Money = {
	amount: 0,
	currency: currentCurrency
};

export const calculatePrice = (
	services: OrderServiceFragment[],
	selectedServices: TravellerSelectedServices[]
): Money => {
	const prices: Money[] = [];

	selectedServices.forEach(passengerServices => {
		passengerServices?.gdsServices?.setServices?.forEach(selectedService => {
			const service = services.find(item => item.id === selectedService.serviceId);
			if (service?.price) {
				prices.push(multiplyMoney(service.price, selectedService.count));
			}
		});
	});

	const total = getTotalPriceUtil(prices, price => price);

	return total.amount > 0 ? total : null;
};

export const getState = (state: CheckoutState) => state.orderPrice;
export const getPrice = createSelector(
	getState,
	({ price }): CheckoutOrderFragment['price'] => price as OrderPriceFragment
);
export const getMealsPrice = createSelector(getPrice, ({ meals }) => meals ?? zeroPrice);
export const getSeatsPrice = createSelector(getPrice, ({ seats }) => seats ?? zeroPrice);
export const getBaggagePrice = createSelector(getPrice, ({ baggage }) => baggage ?? zeroPrice);
export const getEveryTravelPrice = createSelector(getPrice, ({ everyTravel }) => everyTravel ?? zeroPrice);
export const getTotalPrice = createSelector(getPrice, ({ total }) => total ?? zeroPrice);
export const getAeroexpressPrice = createSelector(getPrice, ({ aeroexpress }) => aeroexpress ?? zeroPrice);
export const getInsurancePrice = createSelector(getPrice, ({ insurance }) => insurance ?? zeroPrice);
export const getBusinessLoungePrice = createSelector(getPrice, ({ businessLounge }) => businessLounge ?? zeroPrice);
export const getCalculatedBusinessLoungePrice = createSelector(getBusinessLounge, getSelectedServices, calculatePrice);
export const getCalculatedCapsuleHotelPrice = createSelector(getCapsuleHotel, getSelectedServices, calculatePrice);
export const getCalculatedBaggageDeliveryPrice = createSelector(
	getBaggageDelivery,
	getSelectedServices,
	calculatePrice
);
export const getPriceToPay = createSelector(getState, ({ priceToPay }): CheckoutOrderFragment['priceToPay'] => {
	const priceToPayWithZeroPrice = {};

	Object.keys(priceToPay).forEach(key => {
		priceToPayWithZeroPrice[key] = priceToPay[key] ?? zeroPrice;
	});

	return priceToPayWithZeroPrice as CheckoutOrderFragment['priceToPay'];
});
export const getSeatsPriceToPay = createSelector(getPriceToPay, ({ seats }) => seats ?? zeroPrice);
export const getChargePriceToPay = createSelector(getPriceToPay, ({ charge }) => charge ?? zeroPrice);
export const getAeroexpressPriceToPay = createSelector(getPriceToPay, ({ aeroexpress }) => aeroexpress ?? zeroPrice);
export const getInsurancePriceToPay = createSelector(getPriceToPay, ({ insurance }) => insurance ?? zeroPrice);
export const getBaggagePriceToPay = createSelector(getPriceToPay, ({ baggage }) => baggage ?? zeroPrice);
export const getMealsPriceToPay = createSelector(getPriceToPay, ({ meals }) => meals ?? zeroPrice);
export const getAirticketPriceToPay = createSelector(getPriceToPay, ({ airticket }) => airticket ?? zeroPrice);
export const getTotalPriceToPay = createSelector(getPriceToPay, ({ total }) => total ?? zeroPrice);
export const getCalculatedVipServicesPrice = (services: OrderServiceFragment[]) =>
	createSelector(getSelectedServices, selectedServices => calculatePrice(services, selectedServices));
